'use client';

import { Fragment, useState } from 'react';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import type { AffiliatesTablesDealItem } from '@/sanity/pages/PageAffiliateV2Template';
import Image from 'next/image';
import ribbonHighlight from '@/public/affiliate/ribbon-highlight.jpg';
import arrow from '@/public/affiliate/arrow.svg';

interface DealsTableNewProps {
  table: AffiliatesTablesDealItem['table'];
  dealIndex: number;
  showSideBanner?: boolean;
}

export default function DealsTableNew({
  table,
  dealIndex,
  showSideBanner,
}: DealsTableNewProps) {
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);

  if (!table?.tableItem || table.tableItem.length === 0) return null;

  return (
    <Fragment>
      {/* Desktop */}
      <div className="hidden lg:block relative">
        {showSideBanner && (
          <div className="absolute -left-[3rem] -right-[1.5rem] top-[3.9rem] h-[9.75rem] bg-[#84ACC9] rounded-[1rem] pointer-events-none">
            <Image
              className="!w-[66.25rem] !h-full object-cover mix-blend-luminosity opacity-30 absolute right-0 top-0 rounded-[1rem]"
              src={ribbonHighlight}
              alt="ribbon background"
            />
            <div className="absolute w-[66.25rem] h-full right-0 top-0 bg-gradient-to-r from-[#84ACC9] rounded-[1rem]" />
            <Image
              className="absolute bottom-0 translate-y-[2.5rem] -right-[2.5rem] !w-[5rem]"
              src={arrow}
              alt="arrow"
            />
          </div>
        )}
        <table className="w-full border-collapse relative">
        <tbody className="flex h-full justify-between relative">
          {table.tableItem.map((column, columnIndex) => {
            const isLast = columnIndex === table.tableItem.length - 1;
            const textSize = isLast
              ? 'prose-p:text-[1.75rem]'
              : 'prose-p:text-[2rem]';
            return (
              <tr key={columnIndex} className="h-full flex-1 flex">
                <td className="h-full align-top w-full">
                  <div className="border-b border-black/10">
                    <p
                      className="font-sf text-[#111820]/50 text-[1.5rem] leading-[120%] uppercase"
                      dangerouslySetInnerHTML={{ __html: column.title }}
                    />
                  </div>
                  {column.rows.map((row, rowIndex) => (
                    <Fragment key={rowIndex}>
                      {rowIndex === 0 ? (
                        <div
                          className={`richtext richtext-start prose-strong:text-[#111820] prose-p:text-[#111820]/80 prose-p:font-sf ${textSize} prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] h-[5rem] mt-[2rem] justify-start flex flex-col`}
                        >
                          <PortableText
                            value={row.value ?? []}
                            components={richTextComponent}
                          />
                        </div>
                      ) : (
                        <>
                          <hr className="w-full border-t border-black/10 my-[2.5rem]" />
                          <div
                            className={`richtext richtext-start prose-strong:text-[#111820] prose-p:text-[#111820]/80 prose-p:font-sf ${textSize} prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] h-[5rem] justify-start flex flex-col`}
                          >
                            <PortableText
                              value={row.value ?? []}
                              components={richTextComponent}
                            />
                          </div>
                        </>
                      )}
                    </Fragment>
                  ))}
                </td>
              </tr>
            );
          })}
        </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="w-full lg:hidden space-y-[4rem]">
        {table.tableItem[0] && (
          <div className="mb-[2rem]">
            {table.tableItem[0].title && (
              <p
                className="px-[1rem] font-sf text-[1.5rem] leading-[120%] uppercase [&_br]:hidden [&_br]:after:content-['_'] [&_br]:after:inline-block m-0 mb-[1rem] text-[#111820]/50"
                dangerouslySetInnerHTML={{
                  __html: table.tableItem[0].title.replace(/<br\s*\/?>/gi, ' '),
                }}
              />
            )}
            <div className="w-full max-w-full overflow-x-auto rounded-full border border-[#435269]/[15%] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              <div className="flex gap-[.25rem] p-[.5rem] w-max">
                {table.tableItem[0].rows.map((row, rowIndex) => (
                  <button
                    key={rowIndex}
                    onClick={() => setSelectedRowIndex(rowIndex)}
                    className={`py-[1rem] px-[1.25rem] w-fit rounded-full transition-all ${
                      selectedRowIndex === rowIndex ? 'bg-[#103037]' : ''
                    }`}
                  >
                    <div
                      className={`richtext richtext-start prose-p:font-sf prose-p:text-[2rem] prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] ${
                        selectedRowIndex === rowIndex
                          ? 'prose-p:text-[#FFFFFF]'
                          : 'prose-p:text-[#111820]'
                      }`}
                    >
                      <PortableText
                        value={row.value ?? []}
                        components={richTextComponent}
                      />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="px-[2rem]">
          {table.tableItem.slice(1).map((column, columnIndex) => (
            <div
              key={columnIndex}
              className="flex items-start border-b last:border-b-0 gap-[2rem] mb-[2rem] pb-[2rem] border-black/10"
            >
              <div className="flex-1">
                <p
                  className={`font-sf text-[2rem] leading-[120%] uppercase m-0 ${
                    dealIndex === 0 ? 'text-[#111820]/50' : 'text-[#163E03]/50'
                  }`}
                  dangerouslySetInnerHTML={{ __html: column.title }}
                />
              </div>
              <div className="flex-1">
                <div
                  className={`richtext richtext-start prose-p:font-sf prose-p:text-[2rem] prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] prose-p:m-0 ${
                    dealIndex === 0
                      ? 'prose-strong:text-[#111820] prose-p:text-[#111820]/80'
                      : 'prose-strong:text-[#163E03] prose-p:text-[#163E03]/80'
                  }`}
                >
                  <PortableText
                    value={column.rows[selectedRowIndex]?.value ?? []}
                    components={richTextComponent}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Fragment>
  );
}
